import cn from 'classnames'
import BaseFlyout from 'ext-ui/components/BaseFlyout/BaseFlyout'
import Button from 'ext-ui/components/Button/Button'
import Checkbox from 'ext-ui/components/Checkbox/Checkbox'
import TextAreaInput from 'ext-ui/components/TextAreaInput/TextAreaInput'
import TextInput from 'ext-ui/components/TextInput/TextInput'
import {
  CountryCode,
  getCountryCallingCode,
  isValidPhoneNumber,
} from 'libphonenumber-js'
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { useDispatch } from 'react-redux'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import CountryPhoneDropdown from 'src/components/CountryPhoneDropdown/CountryPhoneDropdown'
import FieldLabel from 'src/components/FieldLabel/FieldLabel'
import PhoneTextInputField from 'src/components/PhoneTextInputField/PhoneTextInputField'
import MtAccountCollection from 'src/feature/MtAccountCollection/MtAccountCollection'
import { useTranslate } from 'src/localization/Trns'
import type { AccountCollectionFlowCompletionResponse } from 'src/queries/payments/payments.types'
import {
  useCompleteVendorAccountCollectionFlow,
  useConfirmVendorAccountCollectionFlow,
  useCreateVendor,
  useCreateVendorAccountCollectionFlow,
  useVendorDetail,
} from 'src/queries/vendors/vendors.queries'
import {
  ConfirmVendorAccountCollectionFlowInput,
  CreateVendorRequest,
  Vendor,
  VendorAccountCollectionFlowResponse,
  VendorStatus,
} from 'src/queries/vendors/vendors.types'

import style, {
  ackBlock,
  ackDescription,
  ackPrimary,
  ackRow,
  ackText,
  confirmationSurface,
  description,
  fields,
  flyoutTitle,
  footer,
  footerActionsEnd,
  footerButtons,
  footerError,
  form,
  header,
  pendingNote,
  phoneField,
  phoneInput,
  phoneRow,
  visuallyHidden,
} from './AddVendorFlyout.style'
import useVendorAccountConfirmation from './useVendorAccountConfirmation'
import { createVendorIdempotencyKey } from './vendorIdempotency'

export interface AddVendorFlyoutProps {
  isOpen: boolean
  onClose: () => void
  organizationId: string
  /** Resume an existing, half-finished Vendor instead of adding a new one.
   * The flyout fetches it and opens at whichever step it stopped at: bank
   * account if it has no collection flow, acknowledgement if its flow is
   * AWAITING_ACKNOWLEDGEMENT, and closes immediately if it is already
   * payable. Omitted means "add a new Vendor" — a blank Step 1. */
  resumeVendorId?: string
  className?: string
}

/** Two steps, one flyout instance (prototype's `mvpStep`): the details form,
 * then the ACH payment step. Run 2 ships Step 1 + a Step 2 placeholder. */
type Step = 'details' | 'payment'

type PaymentStepPhase =
  | 'collecting'
  | 'acknowledgement'
  | 'confirming'
  | 'confirmation-pending'
  | 'confirmation-error'

type SubmittedVendorAccountCollectionFlow = Pick<
  VendorAccountCollectionFlowResponse,
  'flowId'
>

export const VENDOR_ACKNOWLEDGEMENT = {
  version: '2026-07-28',
  heading: 'I acknowledge that...',
  description:
    'These ACH account details are accurate and authorized for use when paying this vendor.',
} as const

/**
 * Reuse the app's established country picker + country-scoped phone input
 * pairing. The picker defaults to US but remains independent from the ACH rail:
 * it describes the vendor contact's phone number, not their bank account.
 */
const DEFAULT_PHONE_ISO_COUNTRY = 'US'

/** Names the regions focus is moved into when Step 2 swaps its subtree. */
const FLYOUT_TITLE_ID = 'add-vendor-flyout-title'

/** Anchored format check (the shared EMAIL_REGEX is unanchored and matches on
 * substrings). Presence is gated separately. */
const isValidEmailFormat = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())

const trimmedOrUndefined = (value: string): string | undefined => {
  const trimmed = value.trim()
  return trimmed === '' ? undefined : trimmed
}

/**
 * Add-Vendor flyout (Bill Pay M2). Routing-free: it owns no URL state — the
 * page owns open/close. Step 1 creates the vendor's Modern Treasury
 * Counterparty (`useCreateVendor`) and advances to Step 2; run 3 replaces the
 * Step 2 placeholder with the real ACH account-collection embed.
 *
 * Chassis: BaseFlyout (focus trap / Escape / overlay dismiss / focus
 * restoration to the triggering CTA are provided by its react-modal) + an
 * in-`children` header/footer, because BaseFlyout has no footer or stepper slot
 * — the AssignAccountOwnerFlyout pattern (DS gap flagged in the PR).
 */
const AddVendorFlyout: React.FC<AddVendorFlyoutProps> = ({
  isOpen,
  onClose,
  organizationId,
  resumeVendorId,
  className,
}) => {
  const translate = useTranslate()

  const [step, setStep] = useState<Step>(resumeVendorId ? 'payment' : 'details')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [externalId, setExternalId] = useState('')
  const [notes, setNotes] = useState('')
  const [contactName, setContactName] = useState('')
  const [phone, setPhone] = useState('')
  const [phoneIsoCountry, setPhoneIsoCountry] = useState(
    DEFAULT_PHONE_ISO_COUNTRY,
  )
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [nameErrorArmed, setNameErrorArmed] = useState(false)
  const [emailErrorKind, setEmailErrorKind] = useState<
    'required' | 'invalid' | null
  >(null)
  const [phoneErrorArmed, setPhoneErrorArmed] = useState(false)

  // Step 2 state.
  const [createdVendor, setCreatedVendor] = useState<Vendor | null>(null)
  const [submittedFlow, setSubmittedFlow] =
    useState<SubmittedVendorAccountCollectionFlow | null>(null)
  const [acknowledged, setAcknowledged] = useState(false)
  // Set once confirmation succeeds, to hold the payment step visually still
  // while the flyout animates away (see the onConfirmed handler below).
  // Cleared with the rest of the view state, off-screen.
  const [confirmationSucceeded, setConfirmationSucceeded] = useState(false)
  const createIdempotencyRef = useRef<{
    fingerprint: string
    key: string
  } | null>(null)
  const activeFlowRef = useRef<VendorAccountCollectionFlowResponse | null>(null)
  const completionStatusRef = useRef<
    'PENDING' | 'AWAITING_ACKNOWLEDGEMENT' | 'CONFIRMED' | null
  >(null)
  const recoveryAttemptedFlowIdRef = useRef<string | null>(null)
  const isOpenRef = useRef(isOpen)
  isOpenRef.current = isOpen
  // Phase A <-> Phase B replaces the focused subtree (the MT embed unmounts),
  // which would drop focus to <body>. Move it to the region that appears
  // instead. tabIndex={-1}: programmatically focusable, not in the tab order.
  const confirmationRegionRef = useRef<HTMLDivElement | null>(null)
  const collectRegionRef = useRef<HTMLDivElement | null>(null)
  const dispatch = useDispatch()

  // The reset is split in two: the refs run on close, the view state on the
  // next open (see both effects below).
  //
  // REFS = the correctness half. Nulling createIdempotencyRef is what makes
  // "closing abandons the create" true — createVendor's onSuccess compares
  // the key it was called with against this ref and bails on a mismatch.
  const clearVendorSessionRefs = useCallback((): void => {
    createIdempotencyRef.current = null
    activeFlowRef.current = null
    completionStatusRef.current = null
    recoveryAttemptedFlowIdRef.current = null
  }, [])

  // VIEW = everything the user can see, so it must NOT run while the flyout
  // is still on screen — rewinding it mid-exit is exactly the flash this
  // avoids.
  const clearVendorSessionView = useCallback((): void => {
    setStep('details')
    setName('')
    setEmail('')
    setExternalId('')
    setNotes('')
    setContactName('')
    setPhone('')
    setPhoneIsoCountry(DEFAULT_PHONE_ISO_COUNTRY)
    setSubmitError(null)
    setNameErrorArmed(false)
    setEmailErrorKind(null)
    setPhoneErrorArmed(false)
    setCreatedVendor(null)
    setSubmittedFlow(null)
    setAcknowledged(false)
    setConfirmationSucceeded(false)
  }, [])

  const createVendor = useCreateVendor(organizationId, {
    // Same flyout instance advances to Step 2 (the new vendor is now in the
    // list as ACTION_REQUIRED via the hook's list invalidation). Keep the vendor
    // so Step 2 can bind its account-collection flow to the id.
    onSuccess: (vendor, variables) => {
      if (createIdempotencyRef.current?.key !== variables.idempotencyKey) {
        return
      }
      createIdempotencyRef.current = null
      setCreatedVendor(vendor)
      setStep('payment')
    },
  })

  const createFlowMutation = useCreateVendorAccountCollectionFlow()
  const completeFlowMutation =
    useCompleteVendorAccountCollectionFlow(organizationId)
  const confirmFlowMutation =
    useConfirmVendorAccountCollectionFlow(organizationId)
  const recoveryVendorQuery = useVendorDetail(
    organizationId,
    isOpen && !createdVendor ? resumeVendorId : undefined,
  )
  const confirmation = useVendorAccountConfirmation({
    confirmFlow: confirmFlowMutation.mutateAsync,
    onConfirmed: () => {
      dispatch(
        createSetIsToastOpen(true, {
          toastMessage: (
            <span>
              <strong>{translate('Vendor added')}</strong>{' '}
              {translate(
                '${vendorName} is now active with ACH payment details on file.',
                { vendorName: createdVendor?.name ?? '' },
              )}
            </span>
          ),
        }),
      )
      // Latch before closing: on success the hook drops to phase 'idle',
      // which would recompute this step to 'acknowledgement' and visibly
      // re-enable the checkbox and CTA while the flyout is already sliding
      // out. This freezes the panel as it looked when the button was clicked.
      setConfirmationSucceeded(true)
      onClose()
    },
  })
  const resetConfirmation = confirmation.reset
  const abortConfirmation = confirmation.abort
  const paymentStepPhase: PaymentStepPhase = confirmationSucceeded
    ? 'confirming'
    : confirmation.phase === 'error'
      ? 'confirmation-error'
      : confirmation.phase === 'confirming'
        ? 'confirming'
        : confirmation.phase === 'pending'
          ? 'confirmation-pending'
          : submittedFlow
            ? 'acknowledgement'
            : 'collecting'

  // The view state resets on OPEN, not on close. The flyout stays mounted and
  // visible for its whole exit transition (BaseFlyout -> react-modal
  // `closeTimeoutMS`), so resetting on close rewinds the panel to an empty
  // Step 1 and flashes it on the way out. Resetting here gives the same
  // guarantee — a reopened flyout never shows the abandoned session — without
  // depending on the transition's duration.
  //
  // useLayoutEffect, declared HERE, both on purpose: React runs every layout
  // effect before any passive one, so a plain useEffect would land after the
  // resume sync below (itself a layout effect) and clobber its
  // `setStep('payment')` back to Step 1. Same effect type + earlier
  // declaration means this wipes the slate first and the resume sync still
  // gets the last word.
  useLayoutEffect(() => {
    if (!isOpen) return
    resetConfirmation()
    clearVendorSessionView()
  }, [isOpen, resetConfirmation, clearVendorSessionView])

  const handleCollectionSubmitted = useCallback(
    (flow: SubmittedVendorAccountCollectionFlow): void => {
      setAcknowledged(false)
      resetConfirmation()
      setSubmittedFlow(flow)
    },
    [resetConfirmation],
  )
  const createFlowForEmbed = useCallback(async () => {
    const flow = await createFlowMutation.mutateAsync(createdVendor?.id ?? '')
    activeFlowRef.current = flow
    return flow
  }, [createFlowMutation, createdVendor])
  const completeFlowForEmbed = useCallback(
    async (
      flowId: string,
    ): Promise<AccountCollectionFlowCompletionResponse> => {
      const result = await completeFlowMutation.mutateAsync({
        vendorId: createdVendor?.id ?? '',
        flowId,
      })
      completionStatusRef.current = result.status
      return {
        status: result.status === 'PENDING' ? 'PENDING' : 'COMPLETED',
      }
    },
    [completeFlowMutation, createdVendor],
  )
  const handleCollectionLinked = useCallback((): void => {
    if (completionStatusRef.current === 'CONFIRMED') {
      onClose()
      return
    }
    const flow = activeFlowRef.current
    if (flow) handleCollectionSubmitted(flow)
  }, [handleCollectionSubmitted, onClose])

  useEffect(() => {
    if (!isOpen || createdVendor || !recoveryVendorQuery.data) return
    const vendor = recoveryVendorQuery.data
    setStep('payment')

    if (
      vendor.status === VendorStatus.Active ||
      vendor.paymentMethod?.status === 'PAYABLE'
    ) {
      onClose()
      return
    }

    const flow = vendor.accountCollectionFlow
    if (!flow) {
      setCreatedVendor(vendor)
      setSubmittedFlow(null)
      return
    }
    if (flow.status === 'AWAITING_ACKNOWLEDGEMENT') {
      setCreatedVendor(vendor)
      handleCollectionSubmitted(flow)
      return
    }
    if (recoveryAttemptedFlowIdRef.current === flow.flowId) return
    recoveryAttemptedFlowIdRef.current = flow.flowId
    void completeFlowMutation
      .mutateAsync({
        vendorId: vendor.id,
        flowId: flow.flowId,
      })
      .then((result) => {
        if (!isOpenRef.current) return
        if (result.status === 'CONFIRMED') {
          onClose()
          return
        }
        setCreatedVendor(vendor)
        if (result.status === 'AWAITING_ACKNOWLEDGEMENT') {
          handleCollectionSubmitted(flow)
        } else {
          setSubmittedFlow(null)
        }
      })
      .catch(() => {
        if (!isOpenRef.current) return
        setCreatedVendor(vendor)
        setSubmittedFlow(null)
      })
  }, [
    completeFlowMutation,
    createdVendor,
    handleCollectionSubmitted,
    isOpen,
    onClose,
    organizationId,
    recoveryVendorQuery.data,
  ])

  useEffect(() => {
    if (step !== 'payment' || !isOpen) return
    if (paymentStepPhase === 'collecting') collectRegionRef.current?.focus()
    else confirmationRegionRef.current?.focus()
  }, [paymentStepPhase, step, isOpen])

  // VendorsPage mounts this flyout once, closed, so the `step` initializer
  // above only ever sees resumeVendorId === undefined. A later row action sets
  // the prop on the already-mounted instance, so the step has to be synced when
  // it opens — otherwise the blank Step 1 form stays on screen for the whole
  // vendor-detail fetch and can be typed into or submitted. useLayoutEffect so
  // the correct step is committed before paint rather than one frame later.
  useLayoutEffect(() => {
    if (!isOpen || !resumeVendorId || createdVendor) return
    setStep('payment')
  }, [isOpen, resumeVendorId, createdVendor])

  // Closing abandons the flow. "Add vendor" always opens a blank Step 1 — it
  // never resumes whatever was in progress last time, because a nav item or a
  // CTA should do the thing it says. This discards only the in-flyout
  // session: a Vendor that Step 1 already persisted still exists, and stays
  // visible in the table as Action Required, which is where resuming belongs
  // rather than in state the user cannot see or dismiss.
  //
  // Only the non-visual half runs here, and it has to be immediate:
  // clearVendorSessionRefs is what makes createVendor's onSuccess bail on a
  // create that lands after close, and abortConfirmation cancels a scheduled
  // confirmation retry. Without the latter, closing during a PENDING confirm
  // would leave the 1s/2s/4s retry chain firing at a closed flyout — and a
  // late CONFIRMED would pop the success toast seconds after the user walked
  // away. `abort` rather than `reset` because reset clears React state, which
  // is exactly what must NOT change while the panel is still animating out.
  //
  // The visible half waits for the next open (see the reset above).
  useEffect(() => {
    if (isOpen) return
    clearVendorSessionRefs()
    abortConfirmation()
  }, [isOpen, clearVendorSessionRefs, abortConfirmation])

  const emailInvalid = email.trim() !== '' && !isValidEmailFormat(email)
  const phoneInvalid =
    phone.trim() !== '' &&
    !isValidPhoneNumber(phone, phoneIsoCountry as CountryCode)

  // canSave === the prototype's `canSave`: Vendor Name present, Vendor Email
  // present AND format-valid, phone valid-if-non-empty. Vendor ID / Mailing
  // Address / Contact Name / Description are all optional — Vendor ID is
  // explicitly NOT required (regression guard: it used to be).
  const canSave =
    name.trim() !== '' &&
    email.trim() !== '' &&
    isValidEmailFormat(email) &&
    !phoneInvalid

  const handleConfirmAndAdd = useCallback((): void => {
    const vendorId = createdVendor?.id
    const flowId = submittedFlow?.flowId
    if (
      !vendorId ||
      !flowId ||
      !acknowledged ||
      paymentStepPhase !== 'acknowledgement' ||
      confirmation.isInFlight
    ) {
      return
    }
    const input: ConfirmVendorAccountCollectionFlowInput = {
      vendorId,
      flowId,
      acknowledged: true,
      acknowledgementCopyVersion: VENDOR_ACKNOWLEDGEMENT.version,
    }
    confirmation.startConfirmation(input)
  }, [
    createdVendor,
    submittedFlow,
    acknowledged,
    paymentStepPhase,
    confirmation.isInFlight,
    confirmation.startConfirmation,
  ])

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault()
    setSubmitError(null)
    if (name.trim() === '') setNameErrorArmed(true)
    if (email.trim() === '') setEmailErrorKind('required')
    else if (emailInvalid) setEmailErrorKind('invalid')
    if (phoneInvalid) setPhoneErrorArmed(true)
    if (!canSave || createVendor.isPending) return
    const request: CreateVendorRequest = {
      name: name.trim(),
      email: email.trim(),
      contactName: trimmedOrUndefined(contactName),
      phone:
        phone.trim() === ''
          ? undefined
          : `+${getCountryCallingCode(phoneIsoCountry as CountryCode)}${phone.trim()}`,
      externalId: trimmedOrUndefined(externalId),
      notes: trimmedOrUndefined(notes),
    }
    const fingerprint = JSON.stringify(request)
    if (createIdempotencyRef.current?.fingerprint !== fingerprint) {
      createIdempotencyRef.current = {
        fingerprint,
        key: createVendorIdempotencyKey(),
      }
    }
    const idempotencyKey = createIdempotencyRef.current.key
    // Keep the create failure on the mutate callback so a failed save surfaces
    // inline and the user stays on Step 1 with fields intact (the flyout does
    // not unmount on error).
    createVendor.mutate(
      { request, idempotencyKey },
      {
        onError: () => {
          if (
            !isOpenRef.current ||
            createIdempotencyRef.current?.key !== idempotencyKey
          ) {
            return
          }
          setSubmitError(
            translate(
              'Something went wrong adding this vendor. Please try again.',
            ),
          )
        },
      },
    )
  }

  const confirmationErrorMessage =
    confirmation.errorKind === 'stale-or-terminal'
      ? translate(
          'This bank account setup can no longer be confirmed. Close this window and start again.',
        )
      : confirmation.errorKind === 'not-found'
        ? translate(
            'This vendor or bank account setup could not be found. Please try again or close this window and start again.',
          )
        : confirmation.errorKind === 'acknowledgement-required'
          ? translate(
              'We could not record your acknowledgement. Please try again.',
            )
          : confirmation.errorKind === 'provider'
            ? translate(
                'We could not confirm this bank account right now. Please try again.',
              )
            : confirmation.errorKind === 'unexpected'
              ? translate(
                  'We received an unexpected confirmation response. Please try again.',
                )
              : null

  const title =
    step === 'details'
      ? translate('Add Vendor')
      : translate('Add ACH Payment Details')
  const stepDescription =
    step === 'details'
      ? translate('Create a vendor with the details needed to pay them.')
      : translate(
          "Enter this vendor's bank account details below. After submitting, review and confirm your acknowledgement.",
        )

  return (
    <BaseFlyout
      className={cn('AddVendorFlyout', className)}
      css={style}
      isOpen={isOpen}
      onClose={onClose}
      aria-label={translate('Add Vendor')}
    >
      <div css={header}>
        <h2 css={flyoutTitle} id={FLYOUT_TITLE_ID}>
          {title}
        </h2>
        <p css={description}>{stepDescription}</p>
      </div>

      {step === 'details' ? (
        <form css={form} onSubmit={handleSubmit} noValidate>
          <div css={fields}>
            <TextInput
              label={translate('Vendor Name')}
              requirement={'required'}
              placeholder={translate('Enter vendor name')}
              value={name}
              onChange={(event) => setName(event.target.value)}
              errorText={
                nameErrorArmed && name.trim() === ''
                  ? translate('Vendor name is required')
                  : undefined
              }
              testId={'add-vendor-name'}
            />
            <TextInput
              label={translate('Vendor Email')}
              requirement={'required'}
              type={'email'}
              inputMode={'email'}
              placeholder={translate('Enter vendor email')}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              errorText={
                emailErrorKind === 'required' && email.trim() === ''
                  ? translate('Vendor email is required')
                  : emailErrorKind === 'invalid' && emailInvalid
                    ? translate('Enter a valid email address')
                    : undefined
              }
              tooltipProps={{
                children: translate(
                  'Payment remittance details are sent to this address every time you pay this vendor.',
                ),
              }}
              testId={'add-vendor-email'}
            />
            <TextInput
              label={translate('Vendor ID')}
              placeholder={translate('Enter vendor ID')}
              value={externalId}
              onChange={(event) => setExternalId(event.target.value)}
              tooltipProps={{
                children: translate(
                  "Your organization's own identifier for this vendor, like an ID from your accounting system.",
                ),
              }}
              testId={'add-vendor-external-id'}
            />
            {/* No `requirement` prop: optional fields carry no marker at all,
                only required ones get the "*". The label-order wrapper that
                used to sit here existed solely to move "(Optional)" in front
                of the tooltip, so it went with it. */}
            <TextInput
              label={translate('Contact Name')}
              placeholder={translate('Enter contact name')}
              value={contactName}
              onChange={(event) => setContactName(event.target.value)}
              tooltipProps={{
                children: translate(
                  'Your accounts payable contact at this vendor — who to reach for billing questions.',
                ),
              }}
              testId={'add-vendor-contact-name'}
            />
            <div css={phoneField}>
              <FieldLabel htmlFor={'vendorPhoneNumber'}>
                {translate('Phone Number')}
              </FieldLabel>
              <div css={phoneRow}>
                <CountryPhoneDropdown
                  id={'vendor'}
                  selectedValue={phoneIsoCountry}
                  chooseOption={setPhoneIsoCountry}
                />
                <PhoneTextInputField
                  id={'vendor'}
                  css={phoneInput}
                  countryIso={phoneIsoCountry}
                  value={phone}
                  onChange={setPhone}
                  errors={
                    phoneErrorArmed && phoneInvalid
                      ? [translate('Enter a valid phone number')]
                      : undefined
                  }
                />
              </div>
            </div>
            <TextAreaInput
              aria-label={translate('Vendor Description')}
              placeholder={translate('Enter vendor description')}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              rows={3}
              testId={'add-vendor-notes'}
            />
          </div>
          <div css={footer}>
            {submitError && (
              <p css={footerError} role={'alert'}>
                {submitError}
              </p>
            )}
            <div css={footerButtons}>
              {/* inlineMuted in the prototype -> ext-ui `no-style` variant. */}
              <Button
                variant={'no-style'}
                type={'button'}
                onClick={onClose}
                data-testid={'add-vendor-cancel'}
              >
                {translate('Cancel')}
              </Button>
              <Button
                variant={'primary'}
                type={'submit'}
                disabled={createVendor.isPending}
                loading={createVendor.isPending}
                data-testid={'add-vendor-submit'}
              >
                {translate('Save and Continue')}
              </Button>
            </div>
          </div>
        </form>
      ) : !createdVendor ? (
        <div
          css={fields}
          ref={confirmationRegionRef}
          tabIndex={-1}
          role={'group'}
          aria-labelledby={FLYOUT_TITLE_ID}
        >
          <p
            css={recoveryVendorQuery.isError ? footerError : pendingNote}
            role={recoveryVendorQuery.isError ? 'alert' : 'status'}
            aria-live={recoveryVendorQuery.isError ? undefined : 'polite'}
          >
            {recoveryVendorQuery.isError
              ? translate(
                  'We could not restore this vendor bank account setup. Close this window and try again.',
                )
              : translate('Restoring this vendor bank account setup…')}
          </p>
        </div>
      ) : paymentStepPhase === 'collecting' ? (
        // Phase A — collecting. The MT embed sits directly in the flyout
        // body, with no wrapper framing it off. The prototype's dashed
        // outline marked the region as Modern Treasury's to design, not a
        // border to ship; the goal is for the embed to read as Extend, which
        // is also why MtAccountCollection themes the iframe with ext-ui
        // tokens. BankAccountsPage embeds the same flow unframed. No flyout
        // footer either: the embed owns its own Submit.
        <div css={fields} ref={collectRegionRef} tabIndex={-1}>
          <MtAccountCollection
            createFlow={createFlowForEmbed}
            completeFlow={completeFlowForEmbed}
            onLinked={handleCollectionLinked}
            // The flyout header already says "Add ACH Payment Details"
            // and describes this exact step, so the embed's own title
            // and subtitle are duplicate copy here.
            showHeading={false}
            // The flyout's field column already pads horizontally; the
            // component's own 40px on top of it inset the MT form well
            // inside Step 1's own inputs.
            flush
          />
        </div>
      ) : (
        // Phase B — the provider accepted the hosted form. Only the user's
        // later checkbox + explicit button activation can start /confirm.
        <div
          css={confirmationSurface}
          ref={confirmationRegionRef}
          tabIndex={-1}
          role={'group'}
          aria-labelledby={FLYOUT_TITLE_ID}
        >
          <div css={fields}>
            {paymentStepPhase === 'confirmation-pending' && (
              <p css={pendingNote} role={'status'} aria-live={'polite'}>
                {confirmation.canRetry
                  ? translate(
                      'This bank account is still being confirmed. Try again to check its status.',
                    )
                  : translate(
                      'This bank account is still being confirmed. This can take a moment.',
                    )}
              </p>
            )}
            {paymentStepPhase === 'confirmation-error' &&
              confirmationErrorMessage && (
                <p css={footerError} role={'alert'}>
                  {confirmationErrorMessage}
                </p>
              )}
            {/* No VISIBLE in-flight note: the embed's submit already created
                the external account at Modern Treasury, so this step is only
                Extend's own acknowledgement and a "confirming this bank
                account" line reads as a second verification step that isn't
                happening. The announcement still has to exist though —
                submitting disables the checkbox and CTA, which drops keyboard
                focus and leaves a screen-reader user with no signal at all
                (WCAG 2.1 AA 4.1.3). So the live region stays, visually
                hidden. The pending/error notes above are visible — those are
                exception paths the user has to act on. */}
            {paymentStepPhase === 'confirming' && (
              <p css={visuallyHidden} role={'status'} aria-live={'polite'}>
                {translate('Adding this vendor…')}
              </p>
            )}
          </div>
          <div css={footer}>
            {submittedFlow ? (
              <div css={ackBlock}>
                <div css={ackRow}>
                  <Checkbox
                    id={'vendor-ack'}
                    checked={acknowledged}
                    disabled={paymentStepPhase !== 'acknowledgement'}
                    onChange={(event) => setAcknowledged(event.target.checked)}
                    aria-label={translate(
                      'I acknowledge these ACH account details are accurate and authorized for use when paying this vendor.',
                    )}
                    data-testid={'vendor-ack-checkbox'}
                  />
                  <label css={ackText} htmlFor={'vendor-ack'}>
                    <span css={ackPrimary}>
                      {translate('I acknowledge that...')}
                    </span>
                    <span css={ackDescription}>
                      {translate(
                        'These ACH account details are accurate and authorized for use when paying this vendor.',
                      )}
                    </span>
                  </label>
                </div>
                <div css={footerActionsEnd}>
                  {confirmation.canRetry ? (
                    <Button variant={'secondary'} onClick={confirmation.retry}>
                      {translate('Try again')}
                    </Button>
                  ) : confirmation.errorKind !== 'stale-or-terminal' ? (
                    <Button
                      variant={'primary'}
                      disabled={
                        paymentStepPhase !== 'acknowledgement' ||
                        !acknowledged ||
                        confirmation.isInFlight
                      }
                      loading={confirmation.isInFlight}
                      onClick={handleConfirmAndAdd}
                      data-testid={'vendor-ack-confirm'}
                    >
                      {translate('Confirm and Add')}
                    </Button>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </BaseFlyout>
  )
}

export default AddVendorFlyout
